<script setup>
import ProgressBar from '@/components/molecules/ProgressBar.vue'
import { ref, computed, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useUserStore } from '@/store/user'
import { useOnboardingStore } from '@/store/onboarding'
import { storeToRefs } from 'pinia'
import { sendEvent } from '@/helpers/cerebro'
import { sendGTagEvent } from '@/helpers/gtag'
import TopBar from '@/components/atoms/TopBar.vue'
const route = useRoute()
const QUIZ_ID = route.query?.qid || 'onboarding-1'
const onboardingStore = useOnboardingStore()
const { onboarding, loading, error } = storeToRefs(onboardingStore)
const { loadOnboarding } = onboardingStore

const steps = computed(() => onboarding.value?.steps)
const currentStep = ref(0)

const currentSection = computed(() => {
  return onboarding.value?.progressSections
    ? onboarding.value?.progressSections.findIndex(
        section =>
          currentStep.value >= section.start && currentStep.value < section.end,
      )
    : ''
})

const responsePage = ref({ show: false })

const userStore = useUserStore()
const answers = computed(() => userStore.getAnswers())

const router = useRouter()

const onSelection = optionIndex => {
  const currentStepObj = steps.value[currentStep.value]

  const value = getValueFromAnswer(optionIndex)

  answers.value[currentStepObj.id ?? currentStepObj.name ?? currentStep.value] =
    {
      answer: optionIndex,
      value,
    }

  if (currentStepObj.responsePage) {
    responsePage.value = {
      show: true,
      ...currentStepObj.responsePage,
    }
  } else if (currentStepObj.dynamicResponse) {
    responsePage.value = {
      show: true,
      ...currentStepObj.props.options[optionIndex].responsePage,
    }
  }
}

const goNextStep = answer => {
  onSelection(answer)
  const currentStepObj = steps.value[currentStep.value]
  sendEvent({
    eventName: 'page_completed',
    properties: {
      page_name: currentStepObj.name,
      page_number: currentStep.value,
      question: currentStepObj.props?.title,
      answer:
        answers.value[
          currentStepObj.id ?? currentStepObj.name ?? currentStep.value
        ].value,
      input_format: currentStepObj.component?.__name,
      quiz_id: QUIZ_ID,
    },
  })
  sendGTagEvent({
    event: 'page_completed',
    page_name: currentStepObj.name,
    step_number: currentStep.value,
  })
  if (currentStepObj.id == 'ageRange') {
    sendGTagEvent({
      event: 'age_collected',
      age: answers.value[currentStepObj.id].value,
    })
  }
  if (currentStep.value === Math.ceil(steps.value.length / 2)) {
    sendGTagEvent({
      event: 'flow_half_completed',
    })
  }
  if (currentStep.value === steps.value.length - 1) {
    router.push('/signup')
  } else {
    currentStep.value++
    sendEvent({
      eventName: 'page_displayed',
      properties: {
        page_name: steps.value[currentStep.value].name,
        page_number: currentStep.value,
        questions: steps.value[currentStep.value].props?.title,
        page_language: 'en',
        quiz_id: QUIZ_ID,
      },
    })
    sendGTagEvent({
      event: 'page_displayed',
      page_name: steps.value[currentStep.value].name,
      step_number: currentStep.value,
    })
  }
}

const goPreviousStep = () => {
  if (responsePage.value.show) {
    responsePage.value.show = false
  } else {
    if (currentStep.value > 0) currentStep.value--
  }
}

onMounted(async () => {
  await loadOnboarding(QUIZ_ID)
  const currentStepObj = steps.value[currentStep.value]
  sendGTagEvent({
    event: 'flow_started',
  })
  sendEvent({
    eventName: 'onboarding_started',
    properties: {
      quiz_id: QUIZ_ID,
    },
  })
  sendEvent({
    eventName: 'page_displayed',
    properties: {
      page_name: currentStepObj.name,
      page_number: currentStep.value,
      questions: currentStepObj.props?.title,
      page_language: 'en',
      quiz_id: QUIZ_ID,
    },
  })
  sendGTagEvent({
    event: 'page_displayed',
    page_name: currentStepObj.name,
    step_number: currentStep.value,
  })
})

const getValueFromAnswer = answer => {
  const currentStepObj = steps.value[currentStep.value]
  if (typeof answer === 'number') {
    return currentStepObj.props?.options[answer] ?? answer
  }
  if (Array.isArray(answer)) {
    return answer.map(index => currentStepObj.props?.options[index])
  }
  return answer
}
</script>

<template>
  <TopBar
    :canGoBack="steps && !steps[currentStep].hideProgress"
    @prevClicked="goPreviousStep"
  ></TopBar>
  <div class="mt-24 survey-container">
    <Transition
      appear
      mode="out-in"
      v-if="steps && !steps[currentStep]?.hideProgress"
    >
      <ProgressBar
        :sections="onboarding?.progressSections"
        :currSectionIdx="currentSection"
        :pageIdx="currentStep"
      />
    </Transition>
    <Transition appear mode="out-in" v-if="steps?.length > 0">
      <component
        :is="{ ...steps[currentStep].component }"
        v-bind="steps[currentStep].props"
        :key="`progress${currentStep}`"
        :prevAnswer="
          answers[
            steps[currentStep].id ?? steps[currentStep].name ?? currentStep
          ]?.answer
        "
        @selection="goNextStep"
        @cta="goNextStep"
      />
    </Transition>
  </div>
</template>

<style scoped>
.survey-container {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  width: 100%;
  max-width: 428px;
  height: calc(100vh - 128px);
  padding: 40px 16px;
  gap: 20px;
}

@supports (height: 100dvh) {
  .survey-container {
    height: calc(100dvh - 128px);
  }
}

.v-enter-active,
.v-leave-active {
  transition: opacity 0.3s ease;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
}

@media screen and (max-width: 480px) {
  .survey-container {
    padding: 0px 0px 40px 0px;
  }
}
</style>
